import { Header, HeaderProps } from '@almbrand/header';
import { useDeviceQuery } from '@almbrand/usedevicequery';
import { PageContext } from 'context/PageContext';
import { useContext } from 'react';
import { NavLink } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import { removeStateFromSessionStorage } from 'utilities';
import pskLogoLight from './assets/psk-logo-light.svg';
import styles from './HeaderComponent.module.scss';

interface BasketIconProps<T> {
	basketIcon?: HeaderProps<T>['basketIcon'];
}

export const HeaderComponent: React.FC<HeaderReference & BasketIconProps<any> & { isPackagesPage?: boolean }> = ({
	basketIcon,
	isPackagesPage,
	logoImage,
	logoUrl,
	loginLabelText,
	loginUrl,
	headerLinks,
}) => {
	const pageContext = useContext(PageContext);
	const { theme } = pageContext ?? {};
	const { isMobile } = useDeviceQuery();
	const customer = useAppSelector((state) => state.customer);

	// Theme specific settings
	const isPrivatsikring = theme === 'Privatsikring-theme';
	const isPrivatsikringPackagePage = isPackagesPage && isPrivatsikring;

	const buttonType = isPrivatsikring ? 'secondary' : 'personal';

	const logoutBrand = isPrivatsikring ? 'privatsikring' : 'codan';
	const logoutLink = `/auth/logout?applicationId=${logoutBrand}&redirectUri=https://www.${logoutBrand}.dk`;
	const handleLogout = () => {
		removeStateFromSessionStorage();
		location.href = logoutLink;
	};

	// Get icon props based on theme
	const getIconProps = (icon: 'lock_closed' | 'lock_open'): Partial<HeaderProps<any>['buttonProps']> => {
		// Show icon on login button always on PSK and only on mobile for Codan
		const shouldShowIcon = (theme === 'Codan-theme' && isMobile) || isPrivatsikring;

		if (!shouldShowIcon) return {};

		return {
			iconPosition: isPrivatsikring ? 'right' : 'left',
			iconProps: {
				themeName: theme,
				icon,
				width: '24',
				height: '24',
			},
		};
	};

	const mapNavigationLinks = (links: Link[]) =>
		links
			.map((link) => ({
				text: link?.text ?? '',
				title: link?.title ?? '',
				url: link?.url ?? '',
				target: link?.target ?? '',
				isExternalLink: link?.isExternalLink ?? false,
				useVueRoute: link?.useVueRoute ?? false,
			}))
			.filter((link) => link.text && link.url);

	// Handle login/logout button based on customer data and theme
	const isCustomerDataReady = customer.isDataFetched && customer.customerProductsFetched;
	const authButton: HeaderProps<any>['buttonProps'] = isCustomerDataReady
		? {
				buttonType,
				children: customer.isLoggedIn ? 'Log ud' : (loginLabelText ?? 'Login'),
				size: 'small',
				...getIconProps(customer.isLoggedIn ? 'lock_open' : 'lock_closed'),
				...(customer.isLoggedIn
					? { onClick: handleLogout }
					: {
							linkButtonSettings: {
								href: loginUrl?.url ?? '/auth/login',
								target: loginUrl?.target ?? '_self',
							},
						}),
			}
		: null;

	return (
		<div className={styles.HeaderComponent}>
			<Header
				logoProps={{
					logoUrl: isPrivatsikringPackagePage ? pskLogoLight : (logoImage?.url ?? ''),
					link: logoUrl?.url ?? '/',
					width: isMobile && isPrivatsikring ? '140px' : 'auto',
				}}
				navigationLinks={headerLinks?.length > 0 && mapNavigationLinks(headerLinks)}
				buttonProps={authButton}
				basketIcon={basketIcon}
				renderChildren={(link): JSX.Element => (
					<NavLink
						key={link?.url}
						to={link?.url}
						title={link?.title}
					>
						{link?.text}
					</NavLink>
				)}
			/>
		</div>
	);
};
